import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import QuoteDialog from "@/components/QuoteDialog";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Millet Export", to: "/millet-export" },
  { label: "Resources", to: "/resources" },
  { label: "About Us", to: "/about" },
  { label: "Food Safety Licenses", to: "/food-safety" },
  { label: "Contact Us", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-cream/85 backdrop-blur-xl border-b border-brand-line"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none group">
          <span className={`font-display text-2xl md:text-3xl font-semibold tracking-tight transition-colors duration-500 ${scrolled ? "text-brand-green" : "text-brand-cream"}`}>
            SDRC <span className="text-brand-gold">Global</span>
          </span>
          <span className={`text-[10px] uppercase tracking-[0.28em] mt-0.5 transition-colors duration-500 ${scrolled ? "text-brand-muted" : "text-brand-cream/70"}`}>
            Indian Agri Exports
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 relative py-1 ${
                  scrolled
                    ? isActive
                      ? "text-brand-green"
                      : "text-brand-charcoal hover:text-brand-green"
                    : isActive
                      ? "text-brand-gold"
                      : "text-brand-cream/90 hover:text-brand-gold"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <QuoteDialog>
            <button
              data-testid="header-quote-btn"
              className="bg-brand-green text-white px-6 py-2.5 rounded-full hover:bg-brand-green-dark transition-colors duration-300 text-sm font-medium tracking-wide"
            >
              Request a Quote
            </button>
          </QuoteDialog>
        </nav>

        <button
          className={`lg:hidden transition-colors duration-500 ${scrolled ? "text-brand-green" : "text-brand-cream"}`}
          onClick={() => setMobileOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-brand-cream border-t border-brand-line px-5 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-base font-medium ${isActive ? "text-brand-green" : "text-brand-charcoal"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <QuoteDialog>
            <button className="mt-2 bg-brand-green text-white px-6 py-3 rounded-full text-sm font-medium w-full">
              Request a Quote
            </button>
          </QuoteDialog>
        </div>
      )}
    </header>
  );
}
